import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { transferConfigurationToClient } from "@src/cloud-connect/node-manager/transfer-configuration-to-client/transfer-configuration-to-client";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { RootState } from "@src/reducers/store";
import {
  emptyAlarmNodeData,
  updateAlarmNodeSelectionData,
  updateAlarmNodeSelectionState,
  updateNodeManagerActiveView,
  updateShowUnsavedStructureForNodeManagerNavigationModal,
  updateStructureWasSaved,
} from "@src/reducers/your-reducer";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";

export const useUnsavedStructureForNodeManagerNavigationModalHook = () => {
  const [saveStructureLoading, setSaveStructureLoading] = useState(false);

  const dispatch = useDispatch();

  const machineStructureReferenceCopy = useSelector(
    (state: RootState) => state.yourSlice.editStructurePageTransferData,
  );

  const activeViewSentToModalForNavigation = useSelector(
    (state: RootState) =>
      state.yourSlice.unsavedStructureForNodeManagerNavigationModal
        .activeViewForNavigation,
  );

  const flushAlarmRowSidePanelInfo = () => {
    dispatch(updateAlarmNodeSelectionState("none"));
    dispatch(updateAlarmNodeSelectionData(emptyAlarmNodeData));
  };

  const restoreMachineStructureNodesetRootNode = () => {
    // preparing the root node in OurNode format from the original machine structure
    const ourNodeRootNodeTree = OurNode.makeObjectTree(
      machineStructureReferenceCopy.nodeset.root_node,
    );

    // calling the instance of the machine structure nodeset
    const machineStructureNodesetInstance = getMachineStructureNodeset();
    // overwriting the root node in OurNode format
    machineStructureNodesetInstance.setRootNode(ourNodeRootNodeTree);

    // getting the overwritten root node and creating a flat map from it
    const rootNodeOurNode = machineStructureNodesetInstance.getRootNode();
    const machineStructureFlatMap = rootNodeOurNode.createMapFromOurNodeTree();
    machineStructureNodesetInstance.setFlatMap(machineStructureFlatMap);

    // getting the initial structure nodeset instance
    const initialStructureNodesetInstance = getInitialStructureNodeset();

    // comparing the initial structure nodeset with the machine structure nodeset
    machineStructureNodesetInstance.compareInitialAndMachineStructureNodesetsAndSetTransferred(
      initialStructureNodesetInstance,
      machineStructureNodesetInstance,
    );

    dispatch(updateStructureWasSaved(true));
  };

  const proceedWithoutSaving = () => {
    closeModal();
    dispatch(updateStructureWasSaved(true));
    flushAlarmRowSidePanelInfo();

    // calling the original machine structure nodeset from redux to restore the OurNode instance
    restoreMachineStructureNodesetRootNode();
    dispatch(updateNodeManagerActiveView(activeViewSentToModalForNavigation));
  };

  const saveStructureAndProceed = async () => {
    // the boolean value is setting true to overwriteReduxStateWithLastSavedStructure
    // needed because we are staying in the node manager and the user might want to discard changes later on
    // and we need the latest saved structure in redux
    await transferConfigurationToClient(true, setSaveStructureLoading);
    closeModal();
    flushAlarmRowSidePanelInfo();
    dispatch(updateNodeManagerActiveView(activeViewSentToModalForNavigation));
    dispatch(updateStructureWasSaved(true));
    navigateTo(Route.NODE_MANAGER_PAGE);
  };

  const closeModal = () => {
    dispatch(updateShowUnsavedStructureForNodeManagerNavigationModal(false));
  };

  return {
    closeModal,
    saveStructureAndProceed,
    proceedWithoutSaving,
    saveStructureLoading,
  };
};
